import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Users, CalendarCheck, Wallet, FileText, MessageSquare, Mail, Inbox, Route, Palette, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/caregivers", label: "Caregivers", icon: Users },
  { to: "/admin/bookings", label: "Bookings & escrow", icon: CalendarCheck },
  { to: "/admin/payouts", label: "Payouts", icon: Wallet },
  { to: "/admin/payroll", label: "Payroll", icon: FileText },
  { to: "/admin/whatsapp", label: "WhatsApp log", icon: MessageSquare },
  { to: "/admin/emails", label: "Email log", icon: Inbox },
  { to: "/admin/waitlist", label: "Waitlist", icon: Mail },
  { to: "/admin/routing", label: "Routing engine", icon: Route },
  { to: "/admin/brand", label: "Brand kit", icon: Palette },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-h-screen bg-brand-ivory" data-testid="admin-layout">
      <aside className="hidden w-64 shrink-0 flex-col bg-brand-navy text-slate-100 lg:flex">
        <div className="px-6 py-6"><Link to="/"><Logo dark /></Link></div>
        <nav className="flex-1 space-y-1 px-3" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <n.icon className="h-4 w-4" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate text-xs text-slate-400">{user && user.email}</p>
          <button onClick={logout} className="mt-3 flex w-full items-center gap-2 rounded-xl bg-white/5 px-3 py-2 text-sm text-slate-200 transition hover:bg-white/10" data-testid="admin-logout-button"><LogOut className="h-4 w-4" />Log out</button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 overflow-x-auto border-b border-brand-line bg-white px-4 py-3 lg:hidden">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${isActive ? "bg-brand-navy text-white" : "text-brand-slate"}`}>{n.label}</NavLink>)}
        </div>
        <main className="mx-auto max-w-6xl px-4 py-8 sm:px-8"><Outlet /></main>
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, desc, action }: { eyebrow: string; title: string; desc?: string; action?: React.ReactNode }) {
  return (
    <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
      <div><p className="text-xs font-semibold uppercase tracking-widest text-brand-emerald">{eyebrow}</p><h1 className="mt-1 font-heading text-3xl font-bold text-brand-navy">{title}</h1>{desc && <p className="mt-1 text-sm text-brand-slate">{desc}</p>}</div>
      {action}
    </div>
  );
}

export function Table({ head, children, testId }: { head: string[]; children: React.ReactNode; testId: string }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-brand-line bg-white" data-testid={testId}>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-brand-slate"><tr>{head.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
